import { useState, useEffect } from 'react';
import { Modal, Alert, Spinner, Badge } from 'react-bootstrap';
import { AppModalCloseButton } from '../common/AppModalCloseButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { sampleService, Sample } from '../../services/sample.service';
import { isSampleLocalMode } from '../../services/systems/sample-local/sampleLocalMode';
import { useAuth, isAdminUser } from '../../hooks/useAuth';
import { canManageSample } from '../../utils/samplePermissions';
import UsageRecordFormModal from './UsageRecordFormModal';
import SampleDetailFactsPanels from './SampleDetailFactsPanels';
import SampleDetailModalActions from './SampleDetailModalActions';
import SampleExternalShareModal from './SampleExternalShareModal';
import {
  canSampleAcceptUsageRecord,
  sampleRemainingQuantityBadgeBg,
  sampleUsageBlockReason,
} from '../../utils/sampleUsageRecords';
import {
  navigateToSampleDetail,
  navigateToUsageHistory,
} from '../../utils/samplePageNavigation';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../../utils/softBreakIdentifier';
import { formatSampleQuantityWithUnit } from '../../utils/sampleUnitLabels';
import { joinLabeledValue } from '../../utils/joinLabeledValue';

interface SampleDetailModalProps {
  show: boolean;
  onHide: () => void;
  sampleId: number | null;
  onEdit?: (sample: Sample) => void;
  onDelete?: (sample: Sample) => void;
  adminViewAll?: boolean;
}

function SampleDetailModal({
  show,
  onHide,
  sampleId,
  onEdit,
  onDelete,
  adminViewAll,
}: SampleDetailModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showExternalShareModal, setShowExternalShareModal] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['samples', 'detail', sampleId, adminViewAll],
    queryFn: () =>
      sampleService.getById(sampleId!, { adminViewAll: adminViewAll || undefined }),
    enabled: !!sampleId && show,
  });

  const addUsageMutation = useMutation({
    mutationFn: (data: {
      quantity: number;
      usageDate: string;
      purpose?: string;
      notes?: string;
      action?: string;
    }) =>
      sampleService.addUsageRecord(sampleId!, data, {
        adminViewAll: adminViewAll || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['samples', 'detail', sampleId] });
      queryClient.invalidateQueries({ queryKey: ['samples'] });
      setShowUsageModal(false);
    },
  });

  const sample: Sample | undefined = data?.data;
  const canManage = sample
    ? canManageSample(sample, user?.id, { isAdmin, adminViewAll })
    : false;
  const readOnlyTitle = t('sample.case_manager.read_only_other_user');

  useEffect(() => {
    if (!show) {
      setShowUsageModal(false);
      setShowExternalShareModal(false);
    }
  }, [show]);

  const handleAddUsage = async (data: {
    quantity: number;
    usageDate: string;
    purpose?: string;
    notes?: string;
    action?: string;
  }) => {
    await addUsageMutation.mutateAsync(data);
  };

  const handleClose = () => {
    setShowUsageModal(false);
    setShowExternalShareModal(false);
    onHide();
  };

  // 子モーダル表示中は簡易詳細を隠す（同一 z-index の二重 backdrop を避ける）
  const childModalOpen = showUsageModal || showExternalShareModal;

  return (
    <>
      <Modal
        dialogClassName="sample-system-dialog sample-detail-dialog"
        show={show && !childModalOpen}
        onHide={handleClose}
        size="xl"
        scrollable
        fullscreen="sm-down"
      >
        <Modal.Header className="sample-detail-modal-header">
          <div className="sample-detail-modal-heading min-w-0 pe-2">
            <Modal.Title
              as="h2"
              className="h5 sample-detail-modal-title mb-0"
              aria-label={
                sample?.name
                  ? `${t('sample.detail_title')} · ${formatEntityDisplayName(sample.name, t)}`
                  : t('sample.detail_title')
              }
            >
              {sample?.name ? (
                softBreakIdentifier(sample.name, t)
              ) : (
                <>
                  <i className="fas fa-info-circle me-2" aria-hidden="true" />
                  {t('sample.detail_title')}
                </>
              )}
            </Modal.Title>
            {sample ? (
              <div className="sample-detail-modal-meta d-flex flex-wrap gap-1 align-items-center mt-1">
                <Badge bg="secondary" className="fw-normal">
                  {joinLabeledValue(t('common.id'), String(sample.id))}
                </Badge>
                <Badge
                  bg={sampleRemainingQuantityBadgeBg(sample)}
                  className="fw-normal sample-detail-hero-qty"
                  title={t('sample.remaining_quantity')}
                >
                  {joinLabeledValue(
                    t('sample.remaining_quantity_short', { defaultValue: '残' }),
                    formatSampleQuantityWithUnit(sample.remainingQuantity, sample.unit, t)
                  )}
                </Badge>
                {sample.disposalDate ? (
                  <Badge bg="dark" className="fw-normal">
                    {t('sample_filter.status_disposed')}
                  </Badge>
                ) : null}
              </div>
            ) : null}
          </div>
          <AppModalCloseButton
            onClick={handleClose}
            ariaLabel={t('common.close')}
            title={t('sample.detail_modal_close_title', {
              defaultValue: '簡易詳細を閉じる',
            })}
          />
        </Modal.Header>
        <Modal.Body className="sample-detail-modal-body">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </Spinner>
            </div>
          ) : isError || !sample ? (
            <Alert variant="danger">
              {error instanceof Error
                ? (error.message.startsWith('api.error.') ||
                  error.message.startsWith('sample.') ||
                  error.message.startsWith('common.')
                    ? t(error.message, { defaultValue: error.message })
                    : error.message)
                : t('sample.load_failed')}
            </Alert>
          ) : (
            <>
              {!canManage && (
                <Alert variant="info" className="mb-2 py-2">
                  {readOnlyTitle}
                </Alert>
              )}
              {!canSampleAcceptUsageRecord(sample) && (
                <Alert variant="warning" className="mb-2 py-2">
                  {sample.disposalDate
                    ? t('sample.already_disposed')
                    : (sampleUsageBlockReason(sample, t) ?? t('sample.cannot_add_usage'))}
                </Alert>
              )}
              <SampleDetailModalActions
                sample={sample}
                canManage={canManage}
                canAddUsage={canSampleAcceptUsageRecord(sample)}
                readOnlyTitle={readOnlyTitle}
                onAddUsage={() => setShowUsageModal(true)}
                onShare={
                  isSampleLocalMode() ? undefined : () => setShowExternalShareModal(true)
                }
                onEdit={
                  onEdit
                    ? (s) => {
                        handleClose();
                        onEdit(s);
                      }
                    : undefined
                }
                onDelete={
                  onDelete
                    ? (s) => {
                        handleClose();
                        onDelete(s);
                      }
                    : undefined
                }
                onOpenFullPage={() => {
                  handleClose();
                  navigateToSampleDetail(navigate, sample.id);
                }}
              />

              <SampleDetailFactsPanels
                sample={sample}
                onViewAllHistory={() => {
                  handleClose();
                  navigateToUsageHistory(navigate, sample.id);
                }}
              />
            </>
          )}
        </Modal.Body>
      </Modal>

      {sample && (
        <UsageRecordFormModal
          show={showUsageModal}
          onHide={() => setShowUsageModal(false)}
          onSubmit={handleAddUsage}
          sampleId={sample.id}
          remainingQuantity={sample.remainingQuantity}
          unit={sample.unit}
          isLoading={addUsageMutation.isPending}
        />
      )}

      <SampleExternalShareModal
        show={showExternalShareModal}
        onHide={() => setShowExternalShareModal(false)}
        sampleId={sampleId}
        adminViewAll={adminViewAll}
      />
    </>
  );
}

export { SampleDetailModal };
export default SampleDetailModal;
